import { catchError, map, finalize, tap } from 'rxjs/operators';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { LeaseAgreementStatus, LeaseAgreementStatusToString, RentalObjectCategory, SendMessageType } from '../../model/const/enums';
import { RentPeriod } from '../../model/rent-period';
import { Company } from '../../model/company';
import { Property } from '../../model/property';
import { DataService } from '../../../core/service/base/data.service';
import { LeaseAgreement } from '../../model/lease-agreement';
import { Building } from '../../model/building';
import { Entrance } from '../../model/entrance';
import { SelectionItem } from '../../../core/component/select/select.component';
import { PagedResult } from '../../../core/model/paged-result';
import { ILetterReceiver, IEmailReceiver, ISmsReceiver } from '../../../rent/service/base/contract';

@Injectable()
export class LeaseAgreementService extends DataService<LeaseAgreement> {
  private readonly _defaultFilterFields: string[] = [
    'company',
    'property',
    'building',
    'entrance',
    'status',
    'contactName',
    'rentalObjectCategory',
  ];

  constructor(client: HttpClient, notification: MessageService, progressBar: ProgressBarService, @Inject(APP_CONFIG) config: IAppConfig) {
    super(client, config, notification, LeaseAgreement, progressBar, 'lease-agreement');
  }

  sendMessageTypeRoute: string;
  sendMessageType: SendMessageType;

  export(ids: string[]): Observable<File> {
    return super.exportDataItems(ids, 'Hyreskontrakt.xlsx');
  }

  exportNoPersonalData(ids: string[]): Observable<File> {
    return super.exportDataItemsExcludePersonalData(ids, 'Hyreskontrakt.xlsx');
  }

  getByPayerSelection(contactId: string): Observable<SelectionItem[]> {
    if (!contactId) {
      return of([]);
    }
    this.progressBar.start();
    return this.client
      .get<any[]>(`${this.endpoint}/payer/${contactId}`)
      .pipe(
        map((items: any[]) => {
          if (items instanceof Array) {
            return items.map(item => new SelectionItem(item.caption, item.id));
          }
          return [];
        })
      )
      .pipe(finalize(() => this.progressBar.stop()));
  }

  getFilterDefinition(): Observable<{
    company: string;
    companies: Company[];
    property: string;
    properties: Property[];
    building: string;
    buildings: Building[];
    entrance: string;
    entrances: Entrance[];
    status: number;
    contactName: string;
    rentalObjectCategory: RentalObjectCategory;
    selectedFields: string[];
    defaultFields: string[];
  }> {
    return this.client
      .get<any>(`${this.endpoint}/filter`)
      .pipe(
        map(res => {
          return {
            company: this.firstSavedFilterValue(res.company),
            companies: Array.isArray(res.companies) ? res.companies.map(x => Object.assign(new Company(), x)) : [],
            property: this.firstSavedFilterValue(res.property),
            properties: Array.isArray(res.properties) ? res.properties.map(x => Object.assign(new Property(), x)) : [],
            building: this.firstSavedFilterValue(res.building),
            buildings: Array.isArray(res.buildings) ? res.buildings.map(x => Object.assign(new Building(), x)) : [],
            entrance: res.entrance ? res.entrance : '',
            entrances: Array.isArray(res.entrances) ? res.entrances.map(x => Object.assign(new Entrance(), x)) : [],
            contactName: res.contactName ? res.contactName : '',
            status: Number.isInteger(res.status) ? res.status.toString() : '9',
            rentalObjectCategory: res.rentalObjectCategory,
            selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._defaultFilterFields,
            defaultFields: this._defaultFilterFields,
          };
        })
      )
      .pipe(
        catchError(error => {
          console.log(error);
          return [];
        })
      );
  }

  private firstSavedFilterValue(value: string | string[] | null | undefined): string {
    return Array.isArray(value) ? value[0] ?? '' : value ?? '';
  }

  getTerminationForm(id: string): Observable<Blob> {
    return this.client.get(`${this.endpoint}/termination-form/${id}`, { responseType: 'blob' }).pipe(
      map(res => {
        return new Blob([res as BlobPart], { type: 'application/pdf' });
      })
    );
  }

  getDwellingLeaseAgreementForm(id: string): Observable<Blob> {
    return this.client.get(`${this.endpoint}/dwelling-leaseagreement-form/${id}`, { responseType: 'blob' }).pipe(
      map(res => {
        return new Blob([res as BlobPart], { type: 'application/pdf' });
      })
    );
  }

  getKeyReceiptForm(context: any): Observable<Blob> {
    return this.client.post(`${this.endpoint}/key-receipt-form`, JSON.stringify(context), { responseType: 'blob' }).pipe(
      map(res => {
        return new Blob([res as BlobPart], { type: 'application/pdf' });
      })
    );
  }

  getParkingLeaseAgreementForm(id: string): Observable<Blob> {
    return this.client.get(`${this.endpoint}/parking-leaseagreement-form/${id}`, { responseType: 'blob' }).pipe(
      map(res => {
        return new Blob([res as BlobPart], { type: 'application/pdf' });
      })
    );
  }

  getPremisesLeaseAgreementForm(id: string): Observable<Blob> {
    return this.client.get(`${this.endpoint}/premises-leaseagreement-form/${id}`, { responseType: 'blob' }).pipe(
      map(res => {
        return new Blob([res as BlobPart], { type: 'application/pdf' });
      })
    );
  }

  getRentPeriods(id: string): Observable<RentPeriod[]> {
    return this.client.get<RentPeriod[]>(`${this.endpoint}/rent-periods/${id}`).pipe(
      map(items => {
        if (items instanceof Array) {
          return items.map(item => {
            var model = Object.assign(new RentPeriod(), item);
            return model;
          });
        }
      })
    );
  }

  getDataForTermination(id: string): Observable<any> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/termination-data/${id}`).pipe(finalize(() => this.progressBar.stop()));
  }

  getDataForESign(id: string): Observable<any> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/esign-data/${id}`).pipe(finalize(() => this.progressBar.stop()));
  }

  getDataForKeySign(id: string): Observable<any> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/esign-key-data/${id}`).pipe(finalize(() => this.progressBar.stop()));
  }

  getDataForVoidOfLeaseAgreement(id: string): Observable<any> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/void-data/${id}`).pipe(finalize(() => this.progressBar.stop()));
  }

  terminate(context: any): Observable<LeaseAgreementStatus | any> {
    return this.client.put<LeaseAgreementStatus>(`${this.endpoint}/terminate`, JSON.stringify(context)).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw error;
      }),
      tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_TERMINATED'))
    );
  }

  previewTerminationForESign(context: any): Observable<Blob> {
    return this.client.post(`${this.endpoint}/terminate-for-esign/preview`, context, { responseType: 'blob' }).pipe(
      map(res => new Blob([res as BlobPart], { type: 'application/pdf' })),
      catchError(error => {
        this.notification.showError(error);
        throw error;
      })
    );
  }

  terminateForESign(context: any): Observable<{ terminationCompleted: boolean }> {
    return this.client.post<{ terminationCompleted: boolean }>(`${this.endpoint}/terminate-for-esign`, context).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw error;
      }),
      tap(result => this.notification.showSuccess(result.terminationCompleted ? 'SUCCESS.LEASE_AGREEMENT_TERMINATED' : 'SUCCESS.ESIGN_SENT'))
    );
  }

  sendKeyReceiptForEsign(context: any): Observable<any> {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(context));
    for (let file of context.attachments) {
      formData.append('attachments[]', file, file.name);
    }

    return this.client.post(`${this.endpoint}/send-key-receipt-for-esign`, formData).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw new Error();
      }),
      tap(() => this.notification.showSuccess('SUCCESS.ESIGN_SENT'))
    );
  }

  sendForEsign(context: any): Observable<any> {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(context));
    for (let file of context.attachments) {
      formData.append('attachments[]', file, file.name);
    }

    return this.client.post(`${this.endpoint}/send-for-esign`, formData).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw new Error();
      }),
      tap(() => this.notification.showSuccess('SUCCESS.ESIGN_SENT'))
    );
  }

  void(context: any): Observable<LeaseAgreementStatus | any> {
    return this.client.put<LeaseAgreementStatus>(`${this.endpoint}/void`, JSON.stringify(context)).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw new Error();
      }),
      tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_VOIDED'))
    );
  }

  getPagedList(filter: any, page: number, pageSize: number, order: string, descending: boolean): Observable<PagedResult<LeaseAgreement>> {
    this.progressBar.start();
    filter.sendMessageType = this.sendMessageType;
    filter = this.prepareFilter(filter);
    return this.client
      .post<PagedResult<LeaseAgreement>>(`${this.endpoint}/${this.sendMessageTypeRoute ? 'page/' + this.sendMessageTypeRoute : 'page'}`, {
        filter: filter,
        page: page,
        pageSize: pageSize,
        orderBy: order,
        descending: descending,
      })
      .pipe(
        map(response => {
          let result = Object.assign(new PagedResult(), response);
          result.items = this.toModels(response.items);
          return result;
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  getEmailFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<IEmailReceiver[]> {
    this.progressBar.start();
    return this.client
      .post<IEmailReceiver[]>(`${this.endpoint}/email-filter`, {
        filter: this.prepareFilter(filter),
        includedIds: includedIds,
        excludedIds: excludedIds,
        affectedItemsCount: affectedItemsCount,
      })
      .pipe(
        // Without this the failure reaches the caller as an unhandled observable error, and the
        // send-message lists show nothing at all: no message, no form, just a stopped progress bar.
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  getLetterFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<ILetterReceiver[]> {
    this.progressBar.start();
    return this.client
      .post<ILetterReceiver[]>(`${this.endpoint}/letter-filter`, {
        filter: this.prepareFilter(filter),
        includedIds: includedIds,
        excludedIds: excludedIds,
        affectedItemsCount: affectedItemsCount,
      })
      .pipe(
        // Without this the failure reaches the caller as an unhandled observable error, and the
        // send-message lists show nothing at all: no message, no form, just a stopped progress bar.
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  getSmsFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<ISmsReceiver[]> {
    this.progressBar.start();
    return this.client
      .post<ISmsReceiver[]>(`${this.endpoint}/sms-filter`, {
        filter: this.prepareFilter(filter),
        includedIds: includedIds,
        excludedIds: excludedIds,
        affectedItemsCount: affectedItemsCount,
      })
      .pipe(
        // Without this the failure reaches the caller as an unhandled observable error, and the
        // send-message lists show nothing at all: no message, no form, just a stopped progress bar.
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }
}
